// Segmented — pill-style selector for 2-4 mutually exclusive options.
// Replaces the bottom-underline tabs pattern for screens where a
// segmented control reads better on mobile (full-width, big tap
// targets, all options visible at a glance).
//
// Renders as <div role="tablist"> with one <button role="tab">
// per option. Active option gets a white "pill" sliding underneath;
// inactive options are transparent. Keyboard: Left/Right arrows move
// the active option, matching ARIA Authoring Practices.

import { useCallback, type KeyboardEvent, type ReactNode } from "react";

import { tokens } from "../tokens/tokens";

export interface SegmentedOption<V extends string> {
  value: V;
  /** Rendered as the tab's contents. A plain string covers the
   *  common case; a ReactNode lets callers compose richer labels
   *  (e.g. "Inbox 3" with the count in a coloured badge). When the
   *  label is a ReactNode, set `ariaLabel` so screen readers don't
   *  have to fall back to the rendered text content. */
  label: ReactNode;
  /** Override the accessible name when `label` is a ReactNode that
   *  doesn't read well as plain text (a number in a coloured badge,
   *  an icon-only label, etc.). When omitted, the tab's accessible
   *  name falls back to the label's text content. */
  ariaLabel?: string;
}

export interface SegmentedProps<V extends string> {
  /** Accessible label for the group (announced by screen readers). */
  ariaLabel: string;
  options: SegmentedOption<V>[];
  value: V;
  onChange: (next: V) => void;
}

export function Segmented<V extends string>({
  ariaLabel,
  options,
  value,
  onChange,
}: SegmentedProps<V>) {
  const onKey = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      const idx = options.findIndex((o) => o.value === value);
      if (idx < 0) return;
      if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        e.preventDefault();
        onChange(options[(idx + 1) % options.length]!.value);
      } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        e.preventDefault();
        onChange(options[(idx - 1 + options.length) % options.length]!.value);
      }
    },
    [options, value, onChange],
  );

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={onKey}
      style={trackStyle}
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={active}
            aria-label={opt.ariaLabel}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(opt.value)}
            style={pillStyle(active)}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

const trackStyle: React.CSSProperties = {
  display: "flex",
  background: "#f1f3f5",
  borderRadius: 12,
  padding: 3,
  gap: 2,
  width: "100%",
};

const pillStyle = (active: boolean): React.CSSProperties => ({
  flex: 1,
  padding: `${tokens.space[2]} ${tokens.space[3]}`,
  borderRadius: 10,
  border: 0,
  background: active ? tokens.color.semantic.surface : "transparent",
  color: active
    ? tokens.color.semantic.heading
    : tokens.color.semantic.textMuted,
  fontWeight: active
    ? tokens.type.weight.semibold
    : tokens.type.weight.medium,
  fontSize: tokens.type.size.sm,
  letterSpacing: "-0.005em",
  cursor: "pointer",
  boxShadow: active
    ? "0 1px 2px rgba(15, 23, 42, 0.06), 0 0 0 1px rgba(15, 23, 42, 0.04)"
    : "none",
  transition: `background ${tokens.motion.durBase} ${tokens.motion.easeOut}, color ${tokens.motion.durBase} ${tokens.motion.easeOut}, box-shadow ${tokens.motion.durBase} ${tokens.motion.easeOut}`,
  minHeight: 40,
});
